import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { SectionCard } from "../../components/Common";
import {
  BedDouble, ArrowRightLeft, Pill, Ban, ClipboardList, FileText,
  Activity, LogOut, StickyNote, Lock, Plus,
} from "lucide-react";
import { RecordVitalsDialog } from "./RecordVitalsDialog";
import { vitalAlerts } from "./vitalsAlerts";

const TYPE_META = {
  admission: { color: "bg-blue-100 text-[#1565C0]",  Icon: BedDouble,      label: "Admission" },
  transfer:  { color: "bg-amber-100 text-[#B45309]", Icon: ArrowRightLeft, label: "Transfer" },
  med_start: { color: "bg-emerald-100 text-[#1B5E20]", Icon: Pill,         label: "Medication started" },
  med_stop:  { color: "bg-red-100 text-[#B71C1C]",   Icon: Ban,            label: "Medication stopped" },
  round:     { color: "bg-violet-100 text-[#6D28D9]",Icon: ClipboardList,  label: "Ward round" },
  round_add: { color: "bg-violet-50 text-[#6D28D9]", Icon: StickyNote,     label: "Ward round addendum" },
  vitals:    { color: "bg-cyan-100 text-[#0E7490]",  Icon: Activity,       label: "Vitals recorded" },
  discharge: { color: "bg-slate-200 text-slate-800", Icon: LogOut,         label: "Discharge" },
  disc_add:  { color: "bg-slate-100 text-slate-700", Icon: StickyNote,     label: "Discharge addendum" },
};

function ago(iso) {
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - d);
    const h = Math.floor(diff / 3600000);
    if (h < 1) {
      const m = Math.floor(diff / 60000);
      return `${m}m ago`;
    }
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    return `${days}d ago`;
  } catch { return ""; }
}

export function AdmissionTimeline({ admission }) {
  const { user } = useAuth();
  const canRecordVitals = ["nurse", "doctor", "hospital_admin", "super_admin"].includes(user?.role) && admission.status === "Admitted";
  const [vitals, setVitals] = useState([]);
  const [showRecord, setShowRecord] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState("all"); // all | rounds | meds | transfers | vitals

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/vitals?patient_id=${encodeURIComponent(admission.patient_id)}`);
        if (alive) setVitals(data || []);
      } catch { if (alive) setVitals([]); }
    })();
    return () => { alive = false; };
  }, [admission.patient_id, reloadKey]);

  const events = useMemo(() => {
    const ev = [];
    if (admission.admitted_at) {
      ev.push({
        type: "admission",
        at: admission.admitted_at,
        title: `Admitted to ${admission.ward}${admission.bed_no ? ` · Bed ${admission.bed_no}` : ""}`,
        by: admission.admitting_doctor || admission.admitted_by_name,
        detail: admission.admitting_diagnosis || admission.reason,
      });
    }
    (admission.transfers || []).forEach((t) => {
      ev.push({
        type: "transfer",
        at: t.at,
        title: `${t.from_ward} → ${t.to_ward}${t.bed_no ? ` · Bed ${t.bed_no}` : ""}`,
        by: t.by,
        detail: t.reason || null,
      });
    });
    (admission.medications || []).forEach((m) => {
      if (m.started_at) {
        ev.push({
          type: "med_start",
          at: m.started_at,
          title: `Started ${m.drug} — ${m.dose} · ${m.route} · ${m.frequency}`,
          by: m.prescribed_by_name,
          detail: m.notes || null,
        });
      }
      if (m.discontinued_at) {
        ev.push({
          type: "med_stop",
          at: m.discontinued_at,
          title: `Discontinued ${m.drug}`,
          by: m.discontinued_by,
          detail: null,
        });
      }
    });
    (admission.ward_rounds || []).forEach((r) => {
      if (r.at) {
        const parts = [r.subjective, r.objective, r.assessment, r.plan].filter(Boolean);
        ev.push({
          type: "round",
          at: r.at,
          title: r.signed ? "Ward round · signed & locked" : "Ward round · draft",
          by: r.doctor_name,
          detail: parts.length > 0 ? parts.join(" · ").slice(0, 220) : null,
        });
      }
      (r.addenda || []).forEach((a) => {
        ev.push({
          type: "round_add",
          at: a.at,
          title: "Ward round addendum",
          by: a.by,
          detail: a.text,
        });
      });
    });
    vitals.forEach((v) => {
      const bp = v.systolic ? `${v.systolic}/${v.diastolic ?? "—"}` : null;
      const bits = [];
      if (bp) bits.push(`BP ${bp}`);
      if (v.pulse != null) bits.push(`HR ${v.pulse}`);
      if (v.temperature != null) bits.push(`T ${Number(v.temperature).toFixed(1)}°C`);
      if (v.spo2 != null) bits.push(`SpO₂ ${v.spo2}%`);
      const alerts = vitalAlerts(v);
      ev.push({
        type: "vitals",
        at: v.created_at,
        title: bits.length ? bits.join(" · ") : "Vitals recorded",
        by: v.recorded_by_name,
        detail: null,
        alerts,
      });
    });
    const d = admission.discharge;
    if (d && d.discharged_at) {
      ev.push({
        type: "discharge",
        at: d.discharged_at,
        title: `Discharged — ${d.condition || "—"}`,
        by: d.discharged_by,
        detail: d.discharge_diagnosis,
      });
      (d.addenda || []).forEach((a) => {
        ev.push({
          type: "disc_add",
          at: a.at,
          title: "Discharge addendum",
          by: a.by,
          detail: a.text,
        });
      });
    }
    // Newest first
    return ev.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [admission, vitals]);

  const FILTER_MAP = {
    all: null,
    rounds: ["round", "round_add"],
    meds: ["med_start", "med_stop"],
    transfers: ["admission", "transfer", "discharge", "disc_add"],
    vitals: ["vitals"],
  };
  const filteredEvents = useMemo(() => {
    const keys = FILTER_MAP[filter];
    if (!keys) return events;
    return events.filter((e) => keys.includes(e.type));
  }, [events, filter]);

  const FILTER_CHIPS = [
    { key: "all",       label: "All" },
    { key: "rounds",    label: "Rounds" },
    { key: "meds",      label: "Meds" },
    { key: "transfers", label: "Transfers" },
    { key: "vitals",    label: "Vitals" },
  ];

  return (
    <SectionCard
      title="Admission timeline"
      testId="admission-timeline"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="timeline-filters">
            {FILTER_CHIPS.map((c) => (
              <button
                key={c.key}
                data-testid={`timeline-filter-${c.key}`}
                type="button"
                onClick={() => setFilter(c.key)}
                className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition ${filter === c.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >{c.label}</button>
            ))}
          </div>
          <span className="text-[11px] text-slate-500">{filteredEvents.length} of {events.length}</span>
          {canRecordVitals && (
            <button
              data-testid="timeline-add-vitals"
              onClick={() => setShowRecord(true)}
              className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5"
            ><Plus className="w-3.5 h-3.5" /> Add vitals</button>
          )}
        </div>
      }
    >
      {filteredEvents.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">
          {events.length === 0 ? "No events yet." : `No ${filter} events.`}
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-6 my-4 mr-4">
          {filteredEvents.map((e, i) => {
            const meta = TYPE_META[e.type] || { color: "bg-slate-100 text-slate-600", Icon: FileText, label: e.type };
            const { Icon } = meta;
            return (
              <li key={`${e.type}-${e.at}-${i}`} className="relative pl-8 pb-5 last:pb-0" data-testid={`timeline-event-${e.type}-${i}`}>
                <span className={`absolute -left-[19px] top-0.5 w-9 h-9 rounded-full ${meta.color} flex items-center justify-center ring-4 ring-white`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">{e.title}</span>
                  {e.type === "round" && e.title.includes("signed") && <Lock className="w-3 h-3 text-[#1B5E20]" />}
                  {e.type === "vitals" && e.alerts?.hasAlert && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#fdecec] text-[#B71C1C] font-semibold uppercase tracking-wide" data-testid={`timeline-vitals-alert-${i}`}>Alert</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 tabular">
                  {new Date(e.at).toLocaleString()} · {ago(e.at)}{e.by ? ` · ${e.by}` : ""}
                </div>
                {e.type === "vitals" && e.alerts?.hasAlert && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.alerts.labels.map((l, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-[#fdecec] text-[#B71C1C] font-medium">{l}</span>
                    ))}
                  </div>
                )}
                {e.detail && (
                  <div className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-3">{e.detail}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {showRecord && (
        <RecordVitalsDialog
          patientId={admission.patient_id}
          onClose={() => setShowRecord(false)}
          onSaved={() => { setShowRecord(false); setReloadKey((k) => k + 1); }}
        />
      )}
    </SectionCard>
  );
}
